import { createFileRoute, redirect } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { formatDateOnly } from '@/lib/dates/date-only';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import { usePortalAccount, type PortalAccount } from '@/hooks/portal/usePortalAccount';
import { PortalShell } from '@/components/portal/PortalShell';
import { PortalPaymentMethodsSection } from '@/components/portal/PortalPaymentMethodsSection';

export const Route = createFileRoute('/(portal)/portal/account/')({
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalAccountPage,
});

function PortalAccountPage() {
  const account = usePortalAccount();

  return (
    <PortalShell>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Account</h1>
        {account.data?.customerSince ? (
          <p className="text-sm text-muted-foreground">
            Customer since {formatDateOnly(account.data.customerSince)}.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Your contact and billing details.</p>
        )}
      </div>

      {account.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : account.isError ? (
        <Card>
          <CardContent className="space-y-3 py-6 text-sm text-muted-foreground">
            <p>We couldn&apos;t load your account. Please try again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void account.refetch()}
              disabled={account.isFetching}
            >
              {account.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <ContactCard account={account.data} />
            <AddressCard account={account.data} />
          </div>
          <PortalPaymentMethodsSection />
        </div>
      )}
    </PortalShell>
  );
}

function CardShell({
  title,
  action,
  children,
}: {
  title: string;
  action?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">{title}</p>
          {action ? (
            <span className="text-xs font-medium text-muted-foreground">{action}</span>
          ) : null}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate text-right font-medium">{value}</span>
    </div>
  );
}

function ContactCard({ account }: { account: PortalAccount }) {
  const c = account.contact;
  return (
    <CardShell title="Contact">
      <div className="space-y-1.5">
        <Row label="Name" value={`${c.firstName} ${c.lastName}`.trim()} />
        {c.email ? <Row label="Email" value={c.email} /> : null}
        {c.phone ? <Row label="Mobile" value={c.phone} /> : null}
      </div>
    </CardShell>
  );
}

export function AddressCard({ account }: { account: PortalAccount }) {
  return (
    <CardShell
      title={account.serviceAddresses.length === 1 ? 'Service address' : 'Service addresses'}
    >
      {account.serviceAddresses.length > 0 ? (
        <ul className="divide-y divide-border text-sm">
          {account.serviceAddresses.map((addr) => (
            <li key={addr.id} className="space-y-1 py-3 first:pt-0 last:pb-0 break-words">
              {addr.label ? <p className="font-medium">{addr.label}</p> : null}
              <p>{addr.street}</p>
              {addr.street2 ? <p>{addr.street2}</p> : null}
              <p className="text-muted-foreground">
                {[addr.city, addr.state, addr.zip].filter(Boolean).join(', ')}
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">No service address on file.</p>
      )}
    </CardShell>
  );
}
